import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import AdminSidebar from '../components/AdminSidebar';
import '../style/common.css';
import './AdminDashboard.css';
import './ViewProgress.css';

function ViewProgress() {
  const [pos, setPOs] = useState([]);
  const [bills, setBills] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPM, setSelectedPM] = useState(null);
  const [viewCompleted, setViewCompleted] = useState(false);
  const [expandedPO, setExpandedPO] = useState(null);
  const [jcFilter, setJcFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { pmId } = useParams();

  useEffect(() => {
    const unsubscribePOs = onSnapshot(collection(db, 'purchase_orders'), (snapshot) => {
      setPOs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubscribeBills = onSnapshot(collection(db, 'ra_bills'), (snapshot) => {
      setBills(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      const allUsers = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(allUsers);
      if (pmId) {
        const foundPM = allUsers.find(u => u.id === pmId);
        if (foundPM) setSelectedPM(foundPM);
      }
    });

    return () => {
      unsubscribePOs();
      unsubscribeBills();
      unsubscribeUsers();
    };
  }, [pmId]);

  const getPMName = (pmId) => {
    const pm = users.find(u => u.id === pmId);
    return pm ? pm.name || pm.email : 'Unassigned';
  };

  const calculateProgress = (po) => {
    const relatedBills = bills.filter(b => b.po_id === po.id);
    const totalApplied = relatedBills.reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);
    const balance = (po.po_total_amount || 0) - totalApplied;
    const status = po.completed ? 'Completed' : balance <= 0 ? 'Completed' : 'Active';
    return { totalApplied, balance, status, bills: relatedBills };
  };

  const togglePO = (poId) => {
    setExpandedPO(prev => (prev === poId ? null : poId));
  };

  const filteredPOs = pos
    .filter(po => po.assigned_to === selectedPM?.id)
    .filter(po => {
      const progress = calculateProgress(po);
      return viewCompleted ? progress.status === 'Completed' : progress.status === 'Active';
    })
    .filter(po => jcFilter ? po.jc_number?.toLowerCase().includes(jcFilter.toLowerCase()) : true)
    .filter(po => {
      if (!startDate && !endDate) return true;
      const poDate = new Date(po.po_date);
      const s = startDate ? new Date(startDate) : null;
      const e = endDate ? new Date(endDate) : null;
      if (s && poDate < s) return false;
      if (e && poDate > e) return false;
      return true;
    });

  return (
    <div className="dashboard-wrapper">
      <AdminSidebar />

      <div className="dashboard-content">
        <h2 className="dashboard-title">Project Progress Overview</h2>

        {!selectedPM ? (
          <>
            <h3>Select a Project Manager</h3>
            <div className="dashboard-stats">
              {users.filter(u => u.role === 'pm').map(pm => (
                <div className="stat-card clickable" key={pm.id} onClick={() => setSelectedPM(pm)}>
                  <h3>{pm.name}</h3>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex-between">
              <h3>POs for {selectedPM.name}</h3>
              <button className="submit-btn" onClick={() => setSelectedPM(null)}>Back to PMs</button>
            </div>

            <div className="filter-bar" style={{ margin: '1rem 0', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search by JC Number"
                value={jcFilter}
                onChange={e => setJcFilter(e.target.value)}
                style={{ padding: '8px', minWidth: '200px' }}
              />
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
              <button
                className="submit-btn"
                onClick={() => setViewCompleted(!viewCompleted)}
              >
                View {viewCompleted ? 'Active' : 'Completed'} POs
              </button>
            </div>

            <div className="po-list">
              {filteredPOs.map(po => {
                const progress = calculateProgress(po);
                const isExpanded = expandedPO === po.id;
                return (
                  <div className="po-card" key={po.id}>
                    <div className="po-card-header" onClick={() => togglePO(po.id)}>
                      <div>
                        <h4>{po.po_number} | JC: {po.jc_number}</h4>
                        <p>Start Date: {po.po_date}</p>
                        <p>Status: <span style={{ color: progress.status === 'Completed' ? 'green' : 'orange' }}>{progress.status}</span></p>
                      </div>
                      <button className="toggle-btn">{isExpanded ? '▲ Hide Details' : '▼ View Details'}</button>
                    </div>

                    {isExpanded && (
                      <div className="po-card-body">
                        <p><strong>Location:</strong> {po.location}</p>
                        <p><strong>Description:</strong> {po.description || 'No description provided'}</p>
                        <p><strong>Total Amount:</strong> ₹{po.po_total_amount?.toFixed(2)}</p>
                        <p><strong>RA Bills Applied:</strong> ₹{progress.totalApplied.toFixed(2)}</p>
                        <p><strong>Balance:</strong> ₹{progress.balance.toFixed(2)}</p>

                        <h4 style={{ marginTop: '1rem' }}>RA Bills:</h4>
                        {progress.bills.length === 0 ? (
                          <p>No RA Bills</p>
                        ) : (
                          <ul className="ra-bill-list">
                            {progress.bills.map(bill => (
                              <li key={bill.id}>
                                <strong>{bill.bill_name || 'Unnamed RA'}:</strong>{' '}
                                ₹{bill.amount} on {new Date(bill.bill_date).toLocaleDateString('en-IN')}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ViewProgress;
